import { useEffect, useState } from 'react'
import { createEntry, listTodos } from '../../api/entries'
import type { Entry } from '../../api/entries'
import type { ColorTheme } from './colorTheme'
import { AssigneeInput } from './AssigneeInput'

interface Props {
  defaultPrivacy: number
  defaultColorTheme: ColorTheme | null
  username: string
  onInserted: (markdown: string) => void
  onClose: () => void
}

function todoLink(entry: Entry, username: string) {
  return `- [ ] [${entry.name}](/~${username}/entry/${entry.id})`
}

export function TodoEntryDialog({ defaultPrivacy, defaultColorTheme, username, onInserted, onClose }: Props) {
  const [mode, setMode] = useState<'new' | 'existing'>('new')
  const [existing, setExisting] = useState<Entry[]>([])
  const [loadingExisting, setLoadingExisting] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [search, setSearch] = useState('')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [assignee, setAssignee] = useState('')
  const [dueDate, setDueDate] = useState('')
  const [privacy, setPrivacy] = useState(defaultPrivacy)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (mode !== 'existing' || loaded) return
    let cancelled = false
    setLoadingExisting(true)
    listTodos(username)
      .then((todos) => { if (!cancelled) { setExisting(todos); setLoaded(true) } })
      .catch((err) => { if (!cancelled) setError(err instanceof Error ? err.message : 'failed to load todos') })
      .finally(() => { if (!cancelled) setLoadingExisting(false) })
    return () => { cancelled = true }
  }, [mode, username, loaded])

  const filtered = existing.filter((entry) => {
    if (entry.todo_status === 'done' || entry.todo_status === 'cancelled') return false
    return entry.name.toLowerCase().includes(search.trim().toLowerCase())
  })

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!title.trim()) return
    setSubmitting(true)
    setError('')
    try {
      const entry = await createEntry({
        name: title.trim(),
        content: description,
        privacy,
        entry_type: 'todo',
        color_theme: defaultColorTheme ?? undefined,
        todo_assignee_username: assignee.trim() || undefined,
        todo_due_date: dueDate || undefined,
      })
      onInserted(todoLink(entry, username))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to create todo')
      setSubmitting(false)
    }
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <form
        onSubmit={handleSubmit}
        style={{ background: 'var(--color-bg)', border: '1px solid var(--color-fg)', padding: '1.5rem', width: '90%', maxWidth: 420 }}
      >
        <p style={{ margin: '0 0 1rem', fontWeight: 'bold' }}>add todo</p>
        <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem' }}>
          <button
            type="button"
            onClick={() => setMode('new')}
            style={{ width: 'auto', background: mode === 'new' ? 'var(--color-1)' : 'transparent', color: 'var(--color-fg)' }}
          >
            new todo
          </button>
          <button
            type="button"
            onClick={() => setMode('existing')}
            style={{ width: 'auto', background: mode === 'existing' ? 'var(--color-1)' : 'transparent', color: 'var(--color-fg)' }}
          >
            existing todo
          </button>
        </div>
        {mode === 'existing' ? (
          <>
            <input
              placeholder="search todos"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ display: 'block', width: '100%', marginBottom: '0.75rem' }}
              autoFocus
            />
            <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid var(--color-fg)', marginBottom: '1rem' }}>
              {loadingExisting && <p style={{ margin: 0, padding: '0.75rem', fontSize: '0.85rem' }}>loading…</p>}
              {!loadingExisting && filtered.length === 0 && (
                <p style={{ margin: 0, padding: '0.75rem', fontSize: '0.85rem', opacity: 0.7 }}>no open todos</p>
              )}
              {filtered.map((entry) => (
                <button
                  key={entry.id}
                  type="button"
                  onClick={() => onInserted(todoLink(entry, username))}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left', background: 'transparent',
                    color: 'var(--color-fg)', border: 'none', borderBottom: '1px solid var(--color-fg)', padding: '0.5rem 0.75rem',
                  }}
                >
                  {entry.name}
                  {entry.todo_status === 'wip' && <span style={{ opacity: 0.7, fontSize: '0.8rem' }}> · wip</span>}
                </button>
              ))}
            </div>
            {error && <p style={{ color: 'var(--color-1)', margin: '0 0 0.75rem', fontSize: '0.85rem' }}>{error}</p>}
            <button type="button" onClick={onClose} style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto' }}>
              cancel
            </button>
          </>
        ) : (
        <>
        <input
          placeholder="todo title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          style={{ display: 'block', width: '100%', marginBottom: '0.75rem' }}
          autoFocus
          required
        />
        <textarea
          placeholder="description (optional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          style={{ display: 'block', width: '100%', marginBottom: '0.75rem' }}
        />
        <AssigneeInput
          value={assignee}
          onChange={setAssignee}
          currentUsername={username}
          style={{ marginBottom: '0.75rem' }}
        />
        <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
          <span style={{ opacity: 0.7 }}>due</span>
          <input
            type="date"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            aria-label="due date"
            style={{ width: 'auto' }}
          />
          {dueDate && (
            <button
              type="button"
              onClick={() => setDueDate('')}
              style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto', fontSize: '0.75rem', padding: '0 0.2rem', opacity: 0.6 }}
            >
              clear
            </button>
          )}
        </label>
        <select
          value={privacy}
          onChange={(e) => setPrivacy(Number(e.target.value))}
          style={{ display: 'block', width: '100%', marginBottom: '1rem' }}
        >
          <option value={0}>private</option>
          <option value={1}>friends</option>
          <option value={2}>friends of friends</option>
          <option value={3}>public</option>
        </select>
        {error && <p style={{ color: 'var(--color-1)', margin: '0 0 0.75rem', fontSize: '0.85rem' }}>{error}</p>}
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <button type="submit" disabled={submitting || !title.trim()}>
            {submitting ? 'creating…' : 'create'}
          </button>
          <button type="button" onClick={onClose} style={{ background: 'transparent', color: 'var(--color-fg)', width: 'auto' }}>
            cancel
          </button>
        </div>
        </>
        )}
      </form>
    </div>
  )
}
